import { DischargeNoteLayout } from './DischargeNoteLayout';
import { DischargeNoteStepProps } from './DischargeNotePage';
import { DischargeNoteForm } from './DischargeNoteForm';
import React, { useCallback, useMemo, useState } from 'react';
import { SessionFormFieldError, UpdateDischargeNoteInput, updateDischargeNote } from '@uplift/api';
import { useAlert, scrollToErrorField, extractServerFormValues } from '@uplift/ui-v2';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import { Formik, FormikErrors, FormikHelpers, yupToFormErrors } from 'formik';
import { DischargeNoteSidebarContent } from './DischargeNoteSidebarContent';
import { DischargeNoteActionBar } from './DischargeNoteActionBar';
import { useHistory } from 'react-router';
import { providerPatientRoute } from '@uplift/constants';
import { dischargeNoteValidation } from './discharge-note.validation';
import { useQueryClient } from '@tanstack/react-query';
import { useDiagnosesByIdsQuery } from '@uplift/queries';


type DischargeNoteFormType = {
  submit: 'later' | 'review' | 'now';
  diagnosis?: string[];
} & Record<string, unknown>;

const getErrors = (values: DischargeNoteFormType, formFieldErrors: SessionFormFieldError[] = []): FormikErrors<DischargeNoteFormType> => {
  let errors = {};

  try {
    dischargeNoteValidation().validateSync(values, { abortEarly: false });
  } catch (error) {
    errors = yupToFormErrors(error);
  }

  const noteErrors = formFieldErrors.reduce((acc, cur) => ({
    ...acc,
    [cur.id]: cur.message
  }), {});

  return { ...errors, ...noteErrors };
};

type ErrorConfig = {
  name: keyof DischargeNoteFormType;
  valueWhenError: unknown;
  errorMessage: string | string[];
};

type GetErrorConfigsArgs = {
  errors: FormikErrors<DischargeNoteFormType>;
  values: DischargeNoteFormType;
};

const getErrorConfigs = ({ errors, values }: GetErrorConfigsArgs): ErrorConfig[] => {
  return Object.entries(errors).reduce((acc, [ name, errorMessage ]) => {
    return [ ...acc, { name, valueWhenError: values[name], errorMessage } ];
  }, Array.of<ErrorConfig>());
};

const ignoreValidationIf = (name: keyof DischargeNoteFormType, values: DischargeNoteFormType) => {
  if ([ 'prognosis', 'continuing_care_recommendations', 'diagnosis' ].includes(name)) {
    return values['reason_for_discharge'] === 'Client did not start treatment';
  }

  return false;
};

type ValidateArgs = {
  values: DischargeNoteFormType;
  errorConfigs: ErrorConfig[];
};

const validate = ({ values, errorConfigs }: ValidateArgs) => {
  return errorConfigs.reduce((acc, { valueWhenError, name, errorMessage }) => {
    if (!ignoreValidationIf(name, values) && isEqual(valueWhenError, values[name])) {
      return {
        ...acc,
        [name]: errorMessage
      };
    }

    return acc;
  }, {});
};

export const FormStep: React.VFC<DischargeNoteStepProps> = props => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const snackbar = useAlert();
  const { data: diagnoses } = useDiagnosesByIdsQuery(props.dischargeNote.diagnosisIds);
  const [ shouldValidate, setShouldValidate ] = React.useState(true);

  const activeDiagnoses = useMemo(() => {
    return (diagnoses ?? []).filter(d => d.archived === false).map(d => d.id);
  }, [ diagnoses ]);

  const initialValues: DischargeNoteFormType = useMemo(() => ({
    ...extractServerFormValues(props.dischargeNote.form.fields),
    diagnosis: activeDiagnoses,
    submit: 'now'
  }), [ activeDiagnoses, props.dischargeNote.form.fields ]);

  const initialErrors = useMemo(() => {
    return getErrors(initialValues, props.dischargeNote.form.errors);
  }, [ initialValues, props.dischargeNote.form.errors ]);

  const [ errorConfigs, setErrorConfigs ] = useState<ErrorConfig[]>(getErrorConfigs({ errors: initialErrors, values: initialValues }));

  const handleSubmit = useCallback(async (values: DischargeNoteFormType, formik: FormikHelpers<DischargeNoteFormType>) => {
    const shouldSubmit = values.submit;
    const input: UpdateDischargeNoteInput['dischargeNoteForm'] = omit(values, [ 'diagnosis', 'submit' ]);

    const response = await updateDischargeNote(props.noteId, {
      dischargeNoteForm: input,
      diagnosisIds: [].concat(values.diagnosis),
    });

    if ('error' in response) {
      snackbar.error('Unable to save note');
      return;
    }

    if (shouldSubmit === 'later') {
      history.push(providerPatientRoute.createLink({ patientId: props.patientId }));
    }

    const errors = getErrors(values, response.data.form.errors);

    if (Object.keys(errors).length) {
      formik.setErrors(errors);
      setErrorConfigs(getErrorConfigs({ errors, values }));
      scrollToErrorField(errors);

      return;
    }

    await queryClient.invalidateQueries({ queryKey: [ 'medical-notes', props.noteId ] });
    props.setStep('review');
  }, [ history, props, queryClient, snackbar ]);


  return (
    <Formik
      initialValues={initialValues}
      initialErrors={initialErrors}
      onSubmit={handleSubmit}
      validate={
        shouldValidate
          ? (values) => validate({ values, errorConfigs })
          : null
      }
    >
      {(formik) => (
        <DischargeNoteLayout
          sidebarContent={<DischargeNoteSidebarContent {...props} />}
          actionBar={(
            <DischargeNoteActionBar
              {...props}
              nextText="next: review note"
              onNext={() => formik.handleSubmit()}
              onSaveAndFinishLater={async () => {
                await formik.setFieldValue('submit', 'later');
                setShouldValidate(false);
                formik.handleSubmit();
                setShouldValidate(true);
              }}
            />
          )}
        >
          <DischargeNoteForm {...props} formFields={props.dischargeNote.form.fields} />
        </DischargeNoteLayout>
      )}
    </Formik>
  );
};
